import { DndContext, type DragEndEvent } from "@dnd-kit/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { printingApi } from "./api";
import { buildSampleTableRows, buildSampleValue } from "./components/CanvasElement";
import { DraggableResizableElement } from "./components/DraggableResizableElement";
import { Toolbox } from "./components/Toolbox";
import { PrintPreview } from "./PrintPreview";
import { findCanvasElement, useDesignerStore } from "./store/useDesignerStore";
import type { BorderStyle, CanvasElement, FontWeight, PageSize, PrintFormatFamily, ReportTypeOption, TextAlign } from "./types";
import { pixelDeltaToGridUnits } from "./gridMath";
import { PageBanner } from "@/shared/components/PageBanner";

const FALLBACK_COLUMN_LABELS: Record<string, string> = {
  sequence_no: "ردیف",
  group_name: "گروه قطعه",
  part_group: "گروه قطعه",
  item_name: "نام کامل قطعه",
  material_name: "نام قطعه",
  supplier: "تولیدکننده / تامین‌کننده",
  manufacturer_supplier: "تولیدکننده / تامین‌کننده",
  brand: "نام تجاری",
  raw_commercial_name: "نام تجاری",
  part_code: "کد فنی",
  material_sku: "کد فنی",
  standard_status: "وضعیت استاندارد",
  remarks: "توضیحات",
  notes: "توضیحات",
  quantity: "تعداد",
  production_uom_display: "واحد سنجش",
  scrap_percent: "درصد ضایعات",
};

export function buildSampleDataFromReportType(
  reportTypeOption: ReportTypeOption | undefined,
  elements: CanvasElement[]
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (!reportTypeOption) return result;

  const fieldsMap = new Map(reportTypeOption.available_fields.map((f) => [f.key, f.label]));
  const tablesMap = new Map(reportTypeOption.available_tables.map((t) => [t.key, t]));

  for (const el of elements) {
    if (el.type === "data_field") {
      const label = fieldsMap.get(el.field_key) || FALLBACK_COLUMN_LABELS[el.field_key] || el.field_key;
      result[el.field_key] = buildSampleValue(label);
    } else if (el.type === "table") {
      const tableOpt = tablesMap.get(el.field_key);
      const colLabelsMap = new Map(tableOpt ? tableOpt.columns.map((c) => [c.key, c.label]) : []);
      const colSchemas = el.columns.map((colKey) => ({
        key: colKey,
        label: colLabelsMap.get(colKey) || FALLBACK_COLUMN_LABELS[colKey] || colKey,
      }));
      result[el.field_key] = buildSampleTableRows(colSchemas, 3);
    } else if (el.type === "group") {
      const repeatSource = el.repeat_source || "items";
      const tableOpt = tablesMap.get(repeatSource);
      const colLabelsMap = new Map(tableOpt ? tableOpt.columns.map((c) => [c.key, c.label]) : []);
      const childFields = (el.children || []).filter((c) => c.type === "data_field");
      const colSchemas = childFields.map((c: any) => ({
        key: c.field_key,
        label: colLabelsMap.get(c.field_key) || FALLBACK_COLUMN_LABELS[c.field_key] || c.field_key,
      }));
      result[repeatSource] = buildSampleTableRows(colSchemas, 3);
    } else if ((el.type === "barcode" || el.type === "qr_code") && el.field_key) {
      const label = fieldsMap.get(el.field_key) || FALLBACK_COLUMN_LABELS[el.field_key] || el.field_key;
      result[el.field_key] = el.content || buildSampleValue(label);
    }
  }

  return result;
}

interface Props {
  family?: PrintFormatFamily;
}

export function PrintFormatDesignerPage({ family: initialFamily }: Props) {
  const { familyId, revisionId } = useParams<{ familyId?: string; revisionId?: string }>();

  const {
    elements,
    pageSize,
    selectedElementId,
    setPageSize,
    updateElement,
    removeElement,
    assignElementToGroup,
    selectElement,
    loadSchema,
  } = useDesignerStore();

  const [family, setFamily] = useState<PrintFormatFamily | null>(initialFamily || null);
  const [reportTypes, setReportTypes] = useState<ReportTypeOption[]>([]);
  const [reportType, setReportType] = useState<string>(initialFamily?.report_type || "");
  const [activeRevisionId, setActiveRevisionId] = useState<string | null>(revisionId || null);
  const [loading, setLoading] = useState<boolean>(!initialFamily);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [customPreviewData, setCustomPreviewData] = useState<Record<string, unknown> | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>("پیش‌نمایش قالب با داده‌های نمونه");

  useEffect(() => {
    printingApi.reportTypes().then(setReportTypes).catch(() => setReportTypes([]));
  }, []);

  useEffect(() => {
    if (initialFamily) {
      setFamily(initialFamily);
      setReportType(initialFamily.report_type);
      setLoading(false);
      return;
    }

    if (!familyId) return;

    setLoading(true);
    Promise.all([printingApi.getFamily(familyId), printingApi.listRevisions(familyId)])
      .then(([fam, revs]) => {
        if (fam) {
          setFamily(fam);
          setReportType(fam.report_type);
        }
        if (Array.isArray(revs)) {
          if (revisionId) {
            const rev = revs.find((r) => r.id === revisionId);
            if (rev) {
              if (rev.status.toLowerCase() === "draft") {
                setActiveRevisionId(rev.id);
              }
              if (Array.isArray(rev.schema_json)) {
                loadSchema(rev.schema_json, (rev.page_size as PageSize) || "A4");
              }
            }
          } else {
            const existingDraft = revs.find((r) => r.status.toLowerCase() === "draft");
            if (existingDraft) {
              setActiveRevisionId(existingDraft.id);
              if (Array.isArray(existingDraft.schema_json)) {
                loadSchema(existingDraft.schema_json, (existingDraft.page_size as PageSize) || "A4");
              }
            }
          }
        }
      })
      .catch((e) => setError("خطا در دریافت اطلاعات قالب: " + String(e)))
      .finally(() => setLoading(false));
  }, [familyId, revisionId, initialFamily]);

  const selectedElement = useMemo(() => {
    function findDeep(items: CanvasElement[], id: string | null): CanvasElement | null {
      if (!id) return null;
      for (const item of items) {
        if (item.id === id) return item;
        if (item.type === "group" && item.children) {
          const found = findDeep(item.children, id);
          if (found) return found;
        }
      }
      return null;
    }
    return findDeep(elements, selectedElementId);
  }, [elements, selectedElementId]);

  const groupElements = useMemo(() => {
    function extractGroups(items: CanvasElement[]): Array<CanvasElement & { type: "group" }> {
      const groups: Array<CanvasElement & { type: "group" }> = [];
      for (const item of items) {
        if (item.type === "group") {
          groups.push(item);
          if (item.children) {
            groups.push(...extractGroups(item.children));
          }
        }
      }
      return groups;
    }
    return extractGroups(elements);
  }, [elements]);

  const currentReportTypeOption = useMemo(
    () => reportTypes.find((rt) => rt.key === reportType),
    [reportTypes, reportType]
  );

  const fieldLabels = useMemo(() => {
    const map: Record<string, string> = {};
    if (currentReportTypeOption) {
      for (const f of currentReportTypeOption.available_fields) {
        map[f.key] = f.label;
      }
    }
    return map;
  }, [currentReportTypeOption]);

  const tableSchemas = useMemo(() => {
    const map: Record<string, Array<{ key: string; label: string }>> = {};
    if (currentReportTypeOption) {
      for (const t of currentReportTypeOption.available_tables) {
        map[t.key] = t.columns;
      }
    }
    return map;
  }, [currentReportTypeOption]);

  const sampleData = useMemo(
    () => buildSampleDataFromReportType(currentReportTypeOption, elements),
    [currentReportTypeOption, elements]
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, delta } = event;
    const element = findCanvasElement(elements, active.id as string);
    if (!element) return;

    const deltaXUnits = pixelDeltaToGridUnits(delta.x);
    const deltaYUnits = pixelDeltaToGridUnits(delta.y);
    if (deltaXUnits === 0 && deltaYUnits === 0) return;

    updateElement(element.id, {
      x: Math.max(0, element.x + deltaXUnits),
      y: Math.max(0, element.y + deltaYUnits),
    });
  }

  async function handleSaveDraft() {
    if (!family) return;
    setSaving(true);
    setError(null);
    try {
      if (activeRevisionId) {
        await printingApi.updateDraftRevision(activeRevisionId, {
          schema_json: elements,
          page_size: pageSize,
        });
        alert("پیش‌نویس با موفقیت به‌روزرسانی شد.");
      } else {
        const newRev = await printingApi.createDraftRevision({
          family: family.id,
          schema_json: elements,
          page_size: pageSize,
        });
        setActiveRevisionId(newRev.id);
        alert("پیش‌نویس جدید با موفقیت ذخیره شد.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  function updateImageSrc(id: string, src: string) {
    updateElement(id, { src });
  }

  if (loading) {
    return <div style={{ padding: "2rem", direction: "rtl" }}>در حال بارگذاری طراح قالب...</div>;
  }

  if (!family) {
    return (
      <div style={{ padding: "2rem", direction: "rtl" }}>
        <p style={{ color: "#b91c1c" }}>خانواده قالب یافت نشد.</p>
        <a href="/printing">بازگشت به لیست خانواده‌ها</a>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", direction: "rtl", gap: "1rem" }}>
      <PageBanner
        title={`طراح قالب: ${family.name}`}
        subtitle={`کد: ${family.code} | نوع گزارش: ${family.report_type}`}
        icon="🎨"
        helpLink="/help/printing/designer"
      >
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
          <a href={`/printing/${family.id}`} style={{ padding: "0.6rem 1rem", background: "rgba(255,255,255,0.15)", borderRadius: "12px", textDecoration: "none", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", fontSize: "0.85rem", fontWeight: 700 }}>
            ← بازگشت به نسخه‌ها
          </a>
          <select
            data-testid="page-size-select"
            value={pageSize}
            onChange={(e) => setPageSize(e.target.value as PageSize)}
            style={{ padding: "0.6rem 1rem", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.3)", background: "rgba(255,255,255,0.15)", color: "#fff", fontWeight: 700, fontSize: "0.85rem", outline: "none" }}
          >
            <option value="A4" style={{ color: "#000" }}>A4</option>
            <option value="A5" style={{ color: "#000" }}>A5</option>
            <option value="LABEL_100x150" style={{ color: "#000" }}>برچسب 100×150mm</option>
          </select>
          <button
            type="button"
            data-testid="preview-sample-btn"
            onClick={() => {
              setCustomPreviewData(null);
              setPreviewTitle("پیش‌نمایش قالب با داده‌های نمونه");
              setShowPreviewModal(true);
            }}
            style={{ padding: "0.6rem 1rem", background: "#059669", color: "#fff", borderRadius: "12px", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 700, fontSize: "0.85rem" }}
          >
            <span>👁️</span> پیش‌نمایش نمونه
          </button>
          <button
            type="button"
            data-testid="preview-real-btn"
            onClick={async () => {
              if (!reportType) return;
              setLoading(true);
              try {
                const data = await printingApi.getRealSampleData(reportType);
                setCustomPreviewData(data);
                setPreviewTitle("پیش‌نمایش قالب با داده‌های واقعی دیتابیس");
                setShowPreviewModal(true);
              } catch (e) {
                setError("خطا در دریافت داده‌های واقعی دیتابیس: " + String(e));
              } finally {
                setLoading(false);
              }
            }}
            style={{ padding: "0.6rem 1rem", background: "#2563eb", color: "#fff", borderRadius: "12px", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 700, fontSize: "0.85rem" }}
          >
            <span>🗄️</span> پیش‌نمایش واقعی
          </button>
          <button
            type="button"
            data-testid="save-draft-btn"
            onClick={handleSaveDraft}
            disabled={saving}
            style={{ padding: "0.6rem 1.25rem", background: "#ffffff", color: "#007270", borderRadius: "12px", border: "none", cursor: saving ? "not-allowed" : "pointer", fontWeight: 700, fontSize: "0.85rem", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)" }}
          >
            {saving ? "در حال ذخیره..." : "ذخیرهٔ پیش‌نویس"}
          </button>
        </div>
      </PageBanner>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Toolbox reportTypes={reportTypes} selectedReportType={reportType} onReportTypeChange={setReportType} />

        <main className="brand-card-extand" style={{ flex: 1, padding: 16, overflow: "auto", position: "relative", marginLeft: "1rem", marginBottom: "1rem", borderRadius: "16px" }}>
        {error && (
          <div role="alert" style={{ color: "#b91c1c", marginBottom: 8, padding: 8, background: "#fee2e2", borderRadius: 6 }}>
            {error}
          </div>
        )}

        <p style={{ color: "#6b7280", fontSize: 12, marginBottom: 8 }}>
          المان را با ماوس جابه‌جا کنید؛ برای تغییر اندازه، دستگیرهٔ گوشهٔ پایین-راست را بکشید.
        </p>

        <DndContext onDragEnd={handleDragEnd}>
          <div
            data-testid="designer-canvas"
            style={{ position: "relative", direction: "ltr", border: "1px solid #d1d5db", width: 800, height: 1100, background: "#fff" }}
          >
            {elements.length === 0 && (
              <p style={{ color: "#9ca3af", padding: 16, direction: "rtl" }}>هنوز المانی اضافه نشده — از پنل سمت راست شروع کنید.</p>
            )}
            {/* Header & Footer boundary guidelines */}
            <div style={{ position: "absolute", top: 140, left: 0, right: 0, borderBottom: "1px dashed #3b82f6", opacity: 0.4, pointerEvents: "none", display: "flex", justifyContent: "flex-end", paddingInlineEnd: 8, fontSize: 10, color: "#2563eb" }}>
              محدودهٔ پایان هدر (Header Bottom Boundary)
            </div>
            <div style={{ position: "absolute", top: 920, left: 0, right: 0, borderTop: "1px dashed #ef4444", opacity: 0.4, pointerEvents: "none", display: "flex", justifyContent: "flex-end", paddingInlineEnd: 8, fontSize: 10, color: "#dc2626" }}>
              محدودهٔ شروع فوتر (Footer Top Boundary)
            </div>

            {elements.map((el) => (
              <DraggableResizableElement
                key={el.id}
                element={el}
                selected={el.id === selectedElementId}
                selectedElementId={selectedElementId}
                onSelect={selectElement}
                onResize={(id, w, h) => updateElement(id, { w, h })}
                fieldLabels={fieldLabels}
                tableSchemas={tableSchemas}
              />
            ))}
          </div>
        </DndContext>
      </main>

      {selectedElement && (
        <aside style={{ width: 250, borderInlineStart: "1px solid #e5e7eb", padding: 12, overflowY: "auto" }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>ویژگی‌های المان</h3>

          {selectedElement.type === "text" && (
            <label style={{ display: "block", marginBottom: 8 }}>
              متن ثابت
              <input
                type="text"
                data-testid="prop-text-content"
                value={selectedElement.content}
                onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </label>
          )}

          {selectedElement.type === "data_field" && (
            <label style={{ display: "block", marginBottom: 8 }}>
              فیلد داده متصل
              <select
                data-testid="prop-data-field-key"
                value={selectedElement.field_key}
                onChange={(e) => updateElement(selectedElement.id, { field_key: e.target.value })}
                style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              >
                {(currentReportTypeOption?.available_fields || []).map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label} ({f.key})
                  </option>
                ))}
              </select>
            </label>
          )}

          {selectedElement.type === "barcode" && (
            <>
              <label style={{ display: "block", marginBottom: 8 }}>
                فیلد متصل به بارکد
                <select
                  data-testid="prop-barcode-field-key"
                  value={selectedElement.field_key || ""}
                  onChange={(e) => updateElement(selectedElement.id, { field_key: e.target.value || undefined })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                >
                  <option value="">(بدون اتصال به فیلد — استفاده از مقدار ثابت)</option>
                  {(currentReportTypeOption?.available_fields || []).map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label} ({f.key})
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                محتوای ثابت / پیش‌فرض
                <input
                  type="text"
                  data-testid="prop-barcode-content"
                  value={selectedElement.content || ""}
                  onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                />
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  data-testid="prop-barcode-show-text"
                  checked={selectedElement.show_text ?? true}
                  onChange={(e) => updateElement(selectedElement.id, { show_text: e.target.checked })}
                />
                <span style={{ fontSize: 12 }}>نمایش متن زیر بارکد</span>
              </label>
            </>
          )}

          {selectedElement.type === "qr_code" && (
            <>
              <label style={{ display: "block", marginBottom: 8 }}>
                فیلد متصل به کد QR
                <select
                  data-testid="prop-qrcode-field-key"
                  value={selectedElement.field_key || ""}
                  onChange={(e) => updateElement(selectedElement.id, { field_key: e.target.value || undefined })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                >
                  <option value="">(بدون اتصال به فیلد — استفاده از مقدار ثابت)</option>
                  {(currentReportTypeOption?.available_fields || []).map((f) => (
                    <option key={f.key} value={f.key}>
                      {f.label} ({f.key})
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                محتوای ثابت / آدرس وب
                <input
                  type="text"
                  data-testid="prop-qrcode-content"
                  value={selectedElement.content || ""}
                  onChange={(e) => updateElement(selectedElement.id, { content: e.target.value })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                />
              </label>
            </>
          )}

          {selectedElement.type === "row_number" && (
            <label style={{ display: "block", marginBottom: 8 }}>
              شروع شماره‌گذاری از
              <input
                type="number"
                data-testid="prop-start-from"
                value={selectedElement.start_from ?? 1}
                onChange={(e) => updateElement(selectedElement.id, { start_from: Number(e.target.value) })}
                style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </label>
          )}

          {selectedElement.type === "group" && (
            <>
              <label style={{ display: "block", marginBottom: 8 }}>
                نام گروه (اختیاری)
                <input
                  type="text"
                  data-testid="prop-group-name"
                  placeholder="مثلاً: قطعات وارداتی"
                  value={selectedElement.name || ""}
                  onChange={(e) => updateElement(selectedElement.id, { name: e.target.value })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                />
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                رابطهٔ دادهای تکرارشونده
                <select
                  data-testid="prop-repeat-source"
                  value={selectedElement.repeat_source || "items"}
                  onChange={(e) => updateElement(selectedElement.id, { repeat_source: e.target.value })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                >
                  {(currentReportTypeOption?.available_tables || [{ key: "items", label: "آیتم‌های BOM" }]).map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                ارتفاع ردیف (گرید)
                <input
                  type="number"
                  min={1}
                  max={10}
                  data-testid="prop-row-height"
                  value={selectedElement.row_height ?? 1}
                  onChange={(e) => updateElement(selectedElement.id, { row_height: Number(e.target.value) })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                />
              </label>
            </>
          )}

          {selectedElement.type === "shape" && (
            <>
              <label style={{ display: "block", marginBottom: 8 }}>
                نوع شکل
                <select
                  data-testid="prop-shape-kind"
                  value={selectedElement.shape_kind || "line_h"}
                  onChange={(e) => updateElement(selectedElement.id, { shape_kind: e.target.value as any })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                >
                  <option value="line_h">خط افقی</option>
                  <option value="line_v">خط عمودی</option>
                  <option value="rect">کادر / مستطیل</option>
                </select>
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                ضخامت خط (px)
                <input
                  type="number"
                  min={1}
                  max={20}
                  step="0.1"
                  data-testid="prop-stroke-width"
                  value={selectedElement.stroke_width ?? 1}
                  onChange={(e) => updateElement(selectedElement.id, { stroke_width: Number(e.target.value) })}
                  style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                />
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                رنگ خط
                <input
                  type="color"
                  data-testid="prop-stroke-color"
                  value={selectedElement.stroke_color || "#000000"}
                  onChange={(e) => updateElement(selectedElement.id, { stroke_color: e.target.value })}
                  style={{ width: "100%", height: 32, padding: 2, marginTop: 2, cursor: "pointer" }}
                />
              </label>
              <label style={{ display: "block", marginBottom: 8 }}>
                نوع خط
                <select
                  data-testid="prop-stroke-style"
                  value={selectedElement.stroke_style || "solid"}
                  onChange={(e) => updateElement(selectedElement.id, { stroke_style: e.target.value as BorderStyle })}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                >
                  <option value="solid">خط ممتد (Solid)</option>
                  <option value="dashed">خط‌چین (Dashed)</option>
                  <option value="dotted">نقطه‌چین (Dotted)</option>
                </select>
              </label>
              {selectedElement.shape_kind === "rect" && (
                <label style={{ display: "block", marginBottom: 8 }}>
                  رنگ پس‌زمینه (Fill)
                  <input
                    type="color"
                    data-testid="prop-fill-color"
                    value={selectedElement.fill_color || "#ffffff"}
                    onChange={(e) => updateElement(selectedElement.id, { fill_color: e.target.value })}
                    style={{ width: "100%", height: 32, padding: 2, marginTop: 2, cursor: "pointer" }}
                  />
                </label>
              )}
            </>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <label style={{ display: "block" }}>
              موقعیت X
              <input
                type="number"
                step="0.1"
                data-testid="prop-x"
                value={selectedElement.x}
                onChange={(e) => updateElement(selectedElement.id, { x: Number(e.target.value) })}
                style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </label>
            <label style={{ display: "block" }}>
              موقعیت Y
              <input
                type="number"
                step="0.1"
                data-testid="prop-y"
                value={selectedElement.y}
                onChange={(e) => updateElement(selectedElement.id, { y: Number(e.target.value) })}
                style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </label>
            <label style={{ display: "block" }}>
              عرض
              <input
                type="number"
                step="0.1"
                data-testid="prop-w"
                value={selectedElement.w}
                onChange={(e) => updateElement(selectedElement.id, { w: Number(e.target.value) })}
                style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </label>
            <label style={{ display: "block" }}>
              ارتفاع
              <input
                type="number"
                step="0.1"
                data-testid="prop-h"
                value={selectedElement.h}
                onChange={(e) => updateElement(selectedElement.id, { h: Number(e.target.value) })}
                style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </label>
          </div>

          {selectedElement.type === "image" && (
            <label style={{ display: "block", marginBottom: 8 }}>
              آدرس تصویر
              <input
                type="text"
                data-testid="prop-image-src"
                value={selectedElement.src}
                placeholder="https://... یا data:image/..."
                onChange={(e) => updateImageSrc(selectedElement.id, e.target.value)}
                style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              />
            </label>
          )}

          {/* Typography Controls */}
          {(selectedElement.type === "text" || selectedElement.type === "data_field" || selectedElement.type === "table") && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>تنظیمات تایپوگرافی و متن</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <label style={{ display: "block" }}>
                  اندازه فونت (px)
                  <input
                    type="number"
                    min={8}
                    max={72}
                    data-testid="prop-font-size"
                    value={selectedElement.font_size ?? 12}
                    onChange={(e) => updateElement(selectedElement.id, { font_size: Number(e.target.value) })}
                    style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                  />
                </label>
                <label style={{ display: "block" }}>
                  ضخامت متن
                  <select
                    data-testid="prop-font-weight"
                    value={selectedElement.font_weight || "normal"}
                    onChange={(e) => updateElement(selectedElement.id, { font_weight: e.target.value as FontWeight })}
                    style={{ width: "100%", padding: 5, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                  >
                    <option value="normal">عادی</option>
                    <option value="bold">برجسته (Bold)</option>
                  </select>
                </label>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <label style={{ display: "block" }}>
                  چیدمان متن
                  <select
                    data-testid="prop-text-align"
                    value={selectedElement.text_align || "right"}
                    onChange={(e) => updateElement(selectedElement.id, { text_align: e.target.value as TextAlign })}
                    style={{ width: "100%", padding: 5, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                  >
                    <option value="right">راست‌چین</option>
                    <option value="center">وسط‌چین</option>
                    <option value="left">چپ‌چین</option>
                  </select>
                </label>
                <label style={{ display: "block" }}>
                  فونت
                  <select
                    data-testid="prop-font-family"
                    value={selectedElement.font_family || "inherit"}
                    onChange={(e) => updateElement(selectedElement.id, { font_family: e.target.value })}
                    style={{ width: "100%", padding: 5, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                  >
                    <option value="inherit">پیش‌فرض سیستم</option>
                    <option value="Vazirmatn, sans-serif">وزیرمتن (Vazir)</option>
                    <option value="Tahoma, sans-serif">تاهما (Tahoma)</option>
                    <option value="Courier New, monospace">فونت کد / یکنواخت</option>
                    <option value="Georgia, serif">سریف (Georgia)</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {/* Border & Background Controls */}
          {selectedElement.type !== "shape" && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>تنظیمات کادر (حاشیه) و پس‌زمینه</h4>
              
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <label style={{ display: "block" }}>
                  نوع کادر
                  <select
                    data-testid="prop-border-style"
                    value={selectedElement.border_style || "none"}
                    onChange={(e) => updateElement(selectedElement.id, { border_style: e.target.value as BorderStyle })}
                    style={{ width: "100%", padding: 5, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                  >
                    <option value="none">بدون کادر (شفاف)</option>
                    <option value="solid">خط ممتد (Solid)</option>
                    <option value="dashed">خط‌چین (Dashed)</option>
                    <option value="dotted">نقطه‌چین (Dotted)</option>
                    <option value="double">دوتایی (Double)</option>
                  </select>
                </label>
                <label style={{ display: "block" }}>
                  ضخامت کادر (px)
                  <input
                    type="number"
                    min={1}
                    max={20}
                    data-testid="prop-border-width"
                    value={selectedElement.border_width ?? 1}
                    onChange={(e) => updateElement(selectedElement.id, { border_width: Number(e.target.value) })}
                    style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                  />
                </label>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                <label style={{ display: "block" }}>
                  رنگ کادر
                  <input
                    type="color"
                    data-testid="prop-border-color"
                    value={selectedElement.border_color || "#000000"}
                    onChange={(e) => updateElement(selectedElement.id, { border_color: e.target.value })}
                    style={{ width: "100%", height: 32, padding: 2, marginTop: 2, cursor: "pointer", border: "1px solid #d1d5db", borderRadius: 4 }}
                  />
                </label>
                <label style={{ display: "block" }}>
                  رنگ پس‌زمینه
                  <input
                    type="color"
                    data-testid="prop-background-color"
                    value={selectedElement.background_color || "#ffffff"}
                    onChange={(e) => updateElement(selectedElement.id, { background_color: e.target.value })}
                    style={{ width: "100%", height: 32, padding: 2, marginTop: 2, cursor: "pointer", border: "1px solid #d1d5db", borderRadius: 4 }}
                  />
                </label>
              </div>
            </div>
          )}

          {/* Group Membership */}
          {selectedElement.type !== "group" && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
              <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>عضویت در گروه</h4>
              <label style={{ display: "block", marginBottom: 8 }}>
                گروه مربوطه
                <select
                  data-testid="prop-group-id"
                  value={selectedElement.group_id || ""}
                  onChange={(e) => assignElementToGroup(selectedElement.id, e.target.value || null)}
                  style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                >
                  <option value="">بدون گروه (عنصر ریشه / Root)</option>
                  {groupElements.map((g, idx) => {
                    const title = g.name ? `گروه ${idx + 1}: ${g.name} (${g.repeat_source})` : `گروه ${idx + 1}: (${g.repeat_source})`;
                    return (
                      <option key={g.id} value={g.id}>
                        {title}
                      </option>
                    );
                  })}
                </select>
              </label>
            </div>
          )}

          {/* Conditional Evaluator */}
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #e5e7eb" }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>تنظیمات ارزیابی شرطی</h4>
            <label style={{ display: "block", marginBottom: 6 }}>
              نمایش وابسته به فیلد
              <select
                data-testid="prop-depends-on"
                value={selectedElement.depends_on_field_key || ""}
                onChange={(e) => updateElement(selectedElement.id, { depends_on_field_key: e.target.value || undefined })}
                style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              >
                <option value="">(بدون شرط وابستگی)</option>
                {(currentReportTypeOption?.available_fields || []).map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label} ({f.key})
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "block", marginBottom: 6 }}>
              نوع ارزیابی و نمایش
              <select
                data-testid="prop-evaluator-type"
                value={selectedElement.evaluator_type || "raw"}
                onChange={(e) => updateElement(selectedElement.id, { evaluator_type: e.target.value as any })}
                style={{ width: "100%", padding: 6, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
              >
                <option value="raw">خام (بدون تغییر / raw)</option>
                <option value="boolean_check">بررسی مقداری / چک‌مارک (✓ یا -)</option>
                <option value="equals_check">برابری با مقدار مشخص</option>
                <option value="not_equals_check">نابرابری با مقدار مشخص</option>
              </select>
            </label>

            {(selectedElement.evaluator_type === "equals_check" || selectedElement.evaluator_type === "not_equals_check") && (
              <label style={{ display: "block", marginBottom: 6 }}>
                مقدار مورد نظر
                <input
                  type="text"
                  data-testid="prop-target-value"
                  value={selectedElement.target_value || ""}
                  onChange={(e) => updateElement(selectedElement.id, { target_value: e.target.value })}
                  style={{ width: "100%", padding: 4, marginTop: 2, borderRadius: 4, border: "1px solid #d1d5db" }}
                />
              </label>
            )}
          </div>

          <button
            type="button"
            data-testid="delete-element-btn"
            onClick={() => removeElement(selectedElement.id)}
            style={{ width: "100%", padding: "6px 12px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", marginTop: 16 }}
          >
            حذف المان
          </button>
        </aside>
      )}

      {showPreviewModal && (
        <div
          data-testid="preview-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 100,
            direction: "rtl",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              padding: 24,
              maxWidth: "92vw",
              maxHeight: "90vh",
              overflow: "auto",
              position: "relative",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, borderBottom: "1px solid #e5e7eb", paddingBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <h3 style={{ fontSize: 16, fontWeight: "bold" }}>{previewTitle}</h3>
                <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 6, padding: 3, gap: 4 }}>
                  <button
                    type="button"
                    data-testid="preview-sample-btn"
                    onClick={() => {
                      setCustomPreviewData(null);
                      setPreviewTitle("پیش‌نمایش قالب با داده‌های نمونه");
                    }}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 4,
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      background: !customPreviewData ? "#059669" : "transparent",
                      color: !customPreviewData ? "#fff" : "#374151",
                    }}
                  >
                    داده‌های نمونه (Sample Data)
                  </button>
                  <button
                    type="button"
                    data-testid="preview-real-btn"
                    onClick={async () => {
                      if (!reportType) return;
                      try {
                        const data = await printingApi.getRealSampleData(reportType);
                        setCustomPreviewData(data);
                        setPreviewTitle("پیش‌نمایش قالب با داده‌های واقعی دیتابیس");
                      } catch (e) {
                        setError("خطا در دریافت داده‌های واقعی دیتابیس: " + String(e));
                      }
                    }}
                    style={{
                      padding: "4px 12px",
                      borderRadius: 4,
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: 600,
                      background: customPreviewData ? "#2563eb" : "transparent",
                      color: customPreviewData ? "#fff" : "#374151",
                    }}
                  >
                    داده‌های واقعی دیتابیس
                  </button>
                </div>
              </div>
              <button
                type="button"
                data-testid="close-preview-modal-btn"
                onClick={() => setShowPreviewModal(false)}
                style={{ padding: "4px 14px", background: "#ef4444", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}
              >
                بستن
              </button>
            </div>
            <PrintPreview elements={elements} pageSize={pageSize} data={customPreviewData || sampleData} />
          </div>
        </div>
      )}
    </div>
  </div>
  );
}

